import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CloudOff, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { flush, pendingEventCount, unreportedDroppedCount } from "../lib/events";
import { clearCachedData } from "../lib/storage";

// Unsent work, never cache: clearing these would silently drop field data.
// (The teacher answer queue lives in IndexedDB, which this never touches.)
// The device id and dropped-event count identify this
// handset's events on the server; resetting them would break gap detection.
const OWNED_KEYS_TO_KEEP = [
  "cerp-event-queue",
  "cerp-attendance-outbox",
  "cerp-device-id",
  "cerp-event-dropped",
];

// The event queue is the app's offline memory. Nothing else in the UI shows
// it, so a facilitator on a bad connection has no way to tell whether the
// day's activity has actually reached the server.
export function SyncCard({ canClear = true }: { canClear?: boolean }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(() => pendingEventCount());
  const [dropped, setDropped] = useState(() => unreportedDroppedCount());
  const [online, setOnline] = useState(() => navigator.onLine);
  const [syncing, setSyncing] = useState(false);

  // The count is a live reading of the queue, not a snapshot from when the
  // screen opened: it should fall as events are sent.
  useEffect(() => {
    const timer = window.setInterval(() => {
      setPending(pendingEventCount());
      setDropped(unreportedDroppedCount());
    }, 2000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  async function syncNow() {
    setSyncing(true);
    await flush();
    setPending(pendingEventCount());
    setDropped(unreportedDroppedCount());
    setSyncing(false);
  }

  function clearCache() {
    clearCachedData(OWNED_KEYS_TO_KEEP);
  }

  return (
    <section className="flex flex-col gap-3 rounded-xl bg-surface p-4 shadow-raised">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-app-ink">{t("syncAndOffline")}</h3>
        <span
          className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ${
            online ? "bg-success-bg text-success-text" : "bg-app-field text-app-muted"
          }`}
        >
          {!online && <CloudOff size={13} />}
          {online ? t("connected") : t("offlineMode")}
        </span>
      </div>

      <dl className="flex flex-col gap-2 text-sm">
        <div className="flex items-center justify-between">
          <dt className="text-app-muted">{t("pendingActivity")}</dt>
          <dd className={`font-semibold ${pending > 0 ? "text-accent" : "text-app-ink"}`}>
            {t("pendingActivityValue", { n: pending })}
          </dd>
        </div>
      </dl>

      {dropped > 0 && (
        <p className="rounded-xl bg-danger-bg p-3 text-xs font-semibold leading-5 text-danger">
          {t("droppedActivityWarning", { n: dropped })}
        </p>
      )}

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          className="flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-app-field text-sm font-semibold text-accent transition-colors hover:bg-accent-soft disabled:text-app-icon"
          onClick={() => void syncNow()}
          disabled={syncing || !online || pending === 0}
        >
          {syncing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
          {t("syncNow")}
        </button>
        {canClear && (
          <button
            type="button"
            className="flex h-10 items-center justify-center gap-2 rounded-xl bg-app-field px-3 text-sm font-semibold text-app-copy transition-colors hover:bg-accent-soft"
            onClick={clearCache}
          >
            <Trash2 size={15} />
            {t("clearCache")}
          </button>
        )}
      </div>
      {canClear && <p className="text-xs leading-5 text-app-muted">{t("clearCacheHelp")}</p>}
    </section>
  );
}
